import { useState, useEffect } from 'react';
import { TimeField } from '../components/ui/time-field';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Clock,
  Layers,
  Loader2,
  CheckCircle,
} from 'lucide-react';

const MobilisationDialog = ({ open, onOpenChange, api, jobId, jobData, equipmentTypes, onComplete }) => {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [excludedStepIds, setExcludedStepIds] = useState([]);
  const [timeOverrides, setTimeOverrides] = useState({}); // { stepId: "HH:MM" }
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState('select'); // 'select' | 'preview' | 'done'

  // Find the equipment type config
  const eqType = equipmentTypes?.find(t => t.name === jobData?.equipment_type);
  const hasSetup = eqType?.requires_setup && (eqType?.setup_profile_ids?.length > 0);

  useEffect(() => {
    if (!open || !hasSetup) return;
    setStep('select');
    setExcludedStepIds([]);
    setTimeOverrides({});
    setPreview(null);
    fetchProfiles();
  }, [open]);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const response = await api.get('/setup-profiles');
      const available = response.data.filter(
        p => p.is_active && (eqType?.setup_profile_ids || []).includes(p.id)
      );
      setProfiles(available);
      // Auto-select default or first
      const defaultId = eqType?.default_setup_profile_id;
      if (defaultId && available.find(p => p.id === defaultId)) {
        setSelectedProfileId(defaultId);
        setSelectedProfile(available.find(p => p.id === defaultId));
      } else if (available.length > 0) {
        setSelectedProfileId(available[0].id);
        setSelectedProfile(available[0]);
      }
    } catch (err) {
      console.error('Failed to fetch profiles', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (id) => {
    setSelectedProfileId(id);
    setSelectedProfile(profiles.find(p => p.id === id) || null);
    setExcludedStepIds([]);
    setTimeOverrides({});
  };

  const toggleStepExclusion = (stepId) => {
    setExcludedStepIds(prev =>
      prev.includes(stepId) ? prev.filter(id => id !== stepId) : [...prev, stepId]
    );
  };

  const getPreviewTime = (offsetMinutes) => {
    const startTime = jobData?.start_time || '07:00';
    const [h, m] = startTime.split(':').map(Number);
    const total = h * 60 + m + offsetMinutes;
    const clamped = Math.max(0, Math.min(total, 23 * 60 + 59));
    return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
  };

  const handleGenerate = async () => {
    if (!selectedProfileId || !jobId) return;
    setGenerating(true);
    try {
      const response = await api.post(`/jobs/${jobId}/apply-setup-profile`, {
        profile_id: selectedProfileId,
        excluded_step_ids: excludedStepIds,
        time_overrides: timeOverrides
      });
      setPreview(response.data);
      setStep('done');
      toast.success(`Generated ${response.data.jobs_created} mobilisation jobs`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate mobilisation');
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    if (step === 'done' && onComplete) {
      onComplete(preview);
    }
  };

  const handleSkip = () => {
    onOpenChange(false);
    if (onComplete) onComplete(null);
  };

  if (!hasSetup) return null;

  const includedSteps = selectedProfile
    ? (selectedProfile.steps || [])
        .filter(s => !excludedStepIds.includes(s.id))
        .sort((a, b) => a.step_order - b.step_order)
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="mobilisation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            {step === 'done' ? 'Mobilisation Generated' : 'Apply Setup Profile'}
          </DialogTitle>
          <DialogDescription>
            {step === 'done'
              ? `${preview?.jobs_created || 0} jobs created and linked to this booking`
              : `"${jobData?.equipment_type}" has a mobilisation profile. Generate supporting bookings?`
            }
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : step === 'done' && preview ? (
          /* Success view */
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">Mobilisation complete</span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Job #</TableHead>
                  <TableHead>Asset Type</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(preview.jobs || []).map(job => (
                  <TableRow key={job.id}>
                    <TableCell className="font-mono">{job.job_number}</TableCell>
                    <TableCell>{job.equipment_type}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        <Clock className="h-3 w-3 mr-1" />{job.start_time}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {job.description}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <DialogFooter>
              <Button onClick={handleClose} data-testid="mobilisation-done-btn">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          /* Selection view */
          <div className="space-y-4">
            {/* Profile selector */}
            {profiles.length > 1 && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">Setup Profile</label>
                <Select value={selectedProfileId} onValueChange={handleProfileChange}>
                  <SelectTrigger data-testid="profile-selector">
                    <SelectValue placeholder="Select profile" />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} ({(p.steps || []).length} steps)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedProfile && (
              <>
                {selectedProfile.description && (
                  <p className="text-sm text-muted-foreground">{selectedProfile.description}</p>
                )}

                {/* Steps preview */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 px-3 py-2 text-sm font-medium">
                    Steps — {includedSteps.length} of {(selectedProfile.steps || []).length} included
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Asset</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead className="w-20">Time</TableHead>
                        <TableHead className="w-24">Include</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selectedProfile.steps || []).sort((a, b) => a.step_order - b.step_order).map(s => {
                        const excluded = excludedStepIds.includes(s.id);
                        return (
                          <TableRow key={s.id} className={excluded ? 'opacity-40' : ''}>
                            <TableCell className="font-mono text-muted-foreground">{s.step_order}</TableCell>
                            <TableCell>{s.asset_type}</TableCell>
                            <TableCell className="font-medium">{s.label}</TableCell>
                            <TableCell>
                              <TimeField
                                value={timeOverrides[s.id] || getPreviewTime(s.offset_minutes)}
                                onChange={v => setTimeOverrides(prev => ({ ...prev, [s.id]: v }))}
                                disabled={excluded}
                                className="w-24"
                                data-testid={`step-time-${s.step_order}`}
                              />
                            </TableCell>
                            <TableCell>
                              {s.optional ? (
                                <Switch
                                  checked={!excluded}
                                  onCheckedChange={() => toggleStepExclusion(s.id)}
                                />
                              ) : (
                                <Badge variant="secondary" className="text-xs">Required</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Job info summary */}
                <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Parent Job</span>
                    <span className="font-medium">{jobData?.description || 'New Job'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Start Date</span>
                    <span>{jobData?.start_date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Start Time</span>
                    <span className="font-mono">{jobData?.start_time}</span>
                  </div>
                </div>
              </>
            )}

            <DialogFooter className="flex justify-between gap-2">
              <Button variant="ghost" onClick={handleSkip} data-testid="skip-mobilisation-btn">
                Skip
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button
                  onClick={handleGenerate}
                  disabled={!selectedProfileId || generating || includedSteps.length === 0}
                  data-testid="generate-mobilisation-btn"
                >
                  {generating ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                  ) : (
                    <><Layers className="h-4 w-4 mr-2" />Generate {includedSteps.length} Jobs</>
                  )}
                </Button>
              </div>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MobilisationDialog;
